"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, ChevronDownIcon, Globe } from "lucide-react";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { ComponentProps } from "react";
function safeHostname(url: string | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
function SourceFavicon(t0) {
  const $ = _c(4);
  const {
    host
  } = t0;
  const [failed, setFailed] = useState(false);
  if (!host || failed) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted"><Globe className="h-3 w-3 text-muted-foreground" /></span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setFailed(true);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1) {
    t3 = <img src={t1} alt="" className="h-5 w-5 shrink-0 rounded-full bg-muted" onError={t2} />;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export type SourcesProps = ComponentProps<"div">;
export const Sources = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("not-prose mb-4 mt-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Collapsible className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type SourcesTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  count: number;
};
export const SourcesTrigger = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let count;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      count,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = count;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    count = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("group/sources inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== count) {
    t2 = children ?? <><Globe className="h-3.5 w-3.5" /><span>{count} source{count === 1 ? "" : "s"}</span><ChevronDownIcon className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/sources:rotate-180" /></>;
    $[7] = children;
    $[8] = count;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <CollapsibleTrigger className={t1} {...props}>{t2}</CollapsibleTrigger>;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export type SourcesContentProps = ComponentProps<typeof CollapsibleContent>;
export const SourcesContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mt-2 grid gap-1.5 sm:grid-cols-2", "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 outline-none data-[state=closed]:animate-out data-[state=open]:animate-in", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CollapsibleContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type SourceProps = ComponentProps<"a">;
export const Source = t0 => {
  const $ = _c(19);
  let children;
  let href;
  let props;
  let title;
  if ($[0] !== t0) {
    ({
      href,
      title,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = href;
    $[3] = props;
    $[4] = title;
  } else {
    children = $[1];
    href = $[2];
    props = $[3];
    title = $[4];
  }
  let t1;
  if ($[5] !== href) {
    t1 = safeHostname(href);
    $[5] = href;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const host = t1;
  let t2;
  if ($[7] !== title) {
    t2 = !!title && !title.includes(" ") && title.includes(".");
    $[7] = title;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const titleIsDomain = t2;
  const label = title ?? host ?? href ?? "";
  let t3;
  if ($[9] !== children || $[10] !== host || $[11] !== label || $[12] !== title || $[13] !== titleIsDomain) {
    t3 = children ?? <><SourceFavicon host={titleIsDomain ? title ?? null : host} /><span className="min-w-0 flex-1"><span className="block truncate text-xs font-medium text-foreground">{label}</span>{!titleIsDomain && host && host !== label ? <span className="block truncate text-[11px] text-muted-foreground">{host}</span> : null}</span><ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/source:opacity-100" /></>;
    $[9] = children;
    $[10] = host;
    $[11] = label;
    $[12] = title;
    $[13] = titleIsDomain;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== href || $[16] !== props || $[17] !== t3) {
    t4 = <a className="group/source flex items-center gap-2.5 rounded-lg border border-border/60 bg-card px-3 py-2 transition-colors hover:border-border hover:bg-accent/40" href={href} rel="noreferrer" target="_blank" {...props}>{t3}</a>;
    $[15] = href;
    $[16] = props;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};